// ─────────────────────────────────────────────────────────────────────────
// detailEmptiness — "is there actually anything to show here?".
//
// Written for a combined ("combineKey") block, where the question is subtler
// than `value == null`. A Passport pack combines a number, an expiry date and
// the uploaded copy under one label. When nothing was ever uploaded the record
// still holds the CONTAINER ({ passportNumber, passportExpiryDate }) — and
// extractDocuments happily turns any object into one nameless "Document" entry,
// so the block printed a file chip for a file that does not exist.
//
// So a file part counts only documents that RESOLVE to something openable (a
// storage location or a URL), and any field may opt into `hideWhenEmpty` from
// Detail Groups to drop out of its block — or drop the block entirely — when it
// has no value. Both rules are generic: no field or module is named here.
//
// ZERO is deliberately NOT empty here: a rate of 0, a count of 0 and a
// percentage of 0 all mean something, and a global "0 is blank" rule would eat
// them. But the END of a range is different — a start/end pair whose end was
// never filled stores 0, and printing it gives "92 · 0" (or a dangling
// separator) for what is really just "92". That is a per-FIELD judgement, not a
// global one, so it is a per-field admin flag: `hideWhenZero` says "for THIS
// field, treat zero as nothing to show". Nothing here knows which field or
// module that is.
// ─────────────────────────────────────────────────────────────────────────

import { extractDocuments } from './renderConfig';

const truthy = (value) => value === true || value === 1 || value === '1';

export function isEmptyValue(value) {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    // A container whose every leaf is empty is empty — the shape exists because
    // the module's update handler always writes the sub-document, not because
    // the user entered anything.
    return Object.values(value).every((v) => isEmptyValue(v));
  }
  return false;
}

/**
 * resolvedDocuments — the documents in a value that can actually be opened.
 * Filtering on location/url is what separates a real upload from the phantom
 * entry extractDocuments derives from a plain scalar container.
 */
export function resolvedDocuments(value) {
  return extractDocuments(value).filter((doc) => doc?.location || doc?.url);
}

export function hasResolvedDocuments(value) {
  return resolvedDocuments(value).length > 0;
}

/**
 * hidesWhenEmpty — the admin flag (Detail Groups → field → "Hide when empty").
 * Accepts the detail-only variant too, so a field can stay visible in the form
 * while disappearing from the detail view when unset.
 */
export function hidesWhenEmpty(field) {
  // Same two homes as hidesWhenZero below: the admin authors this on the
  // detail-config field, but the detail view reads its flags off the FORM field
  // in the getFormGroups response, so the backend forwards it inside
  // renderOptions. Reading both is what actually made this switch work — the key
  // was honoured by the frontend but existed nowhere in Go, so it was discarded
  // on save and never reached the client. The control looked functional and did
  // nothing.
  return truthy(field?.hideWhenEmpty)
    || truthy(field?.hideWhenEmptyInDetail)
    || truthy(field?.renderOptions?.hideWhenEmpty)
    || truthy(field?.renderOptions?.hideWhenEmptyInDetail);
}

/**
 * hidesWhenZero — the admin flag (Detail Groups → field → "…also when it is 0").
 * Independent of `hideWhenEmpty` rather than an extension of it: a field flagged
 * this way hides on 0 AND on blank, so an admin only ever has to reach for one
 * switch to get "show it only when it says something". It stands alone on
 * purpose — a field that opts in must not need a second flag set correctly
 * elsewhere for the range to stop printing its dangling "- 0".
 */
export function hidesWhenZero(field) {
  // Two homes, one meaning. The admin authors `hideWhenZero` on the detail-config
  // field, but the detail view reads its flags off the FORM field in the
  // getFormGroups response — and that struct has no key of its own for this, so
  // the backend forwards it inside renderOptions (the bag that exists so a new
  // display rule needs no schema change). Reading both means the flag works
  // whichever way it arrives, today and if it is ever promoted to a first-class
  // field key.
  return truthy(field?.hideWhenZero) || truthy(field?.renderOptions?.hideWhenZero);
}

/**
 * isZeroValue — a numeric zero, however it was stored. Mongo returns the number
 * 0, a form post returns the string "0", and both mean the same "never filled
 * in" for a flagged field. SCALARS ONLY: a container is judged leaf-by-leaf by
 * isEmptyValue, and calling a whole object "zero" because one leaf is 0 would
 * hide packs that still have something to say.
 */
export function isZeroValue(value) {
  if (typeof value === 'number') return value === 0;
  if (typeof value === 'string') {
    const s = value.trim();
    return s !== '' && Number.isFinite(Number(s)) && Number(s) === 0;
  }
  return false;
}

/**
 * skipEmptyCell — should this cell be omitted entirely?
 * Only ever true when the admin asked for it: an unflagged empty field keeps
 * rendering the configured empty placeholder — and an unflagged ZERO keeps
 * rendering as "0" — exactly as before.
 */
export function skipEmptyCell(field, value) {
  const zeroIsEmpty = hidesWhenZero(field);
  if (!hidesWhenEmpty(field) && !zeroIsEmpty) return false;
  const isFile = field?.type === 'file' || field?.type === 'document' || field?.renderType === 'document';
  if (isFile) return !hasResolvedDocuments(value);
  return isEmptyValue(value) || (zeroIsEmpty && isZeroValue(value));
}
